import { useEffect, useRef, useState } from 'react';
import { createCall, setOffer, setAnswer, subscribeCall, addIceCandidate, subscribeIceCandidates, endCall } from '../services/calls';

export type UseWebRTCParams = {
  callerId: string;
  calleeId: string;
  type: 'audio' | 'video';
  isCaller?: boolean;
  callId?: string;
};

export function useWebRTC(params: UseWebRTCParams) {
  const { callerId, calleeId, type, isCaller = true, callId: externalCallId } = params;
  const [callId, setCallId] = useState<string | null>(externalCallId || null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);

  useEffect(() => {
    let unsubCall: any = null;
    let unsubCallerCandidates: any = null;
    let unsubCalleeCandidates: any = null;

    async function setup() {
      // Build ICE server list with optional TURN via env
      const iceServers: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];
      const turnUrl = (import.meta as any).env?.VITE_TURN_URL as string | undefined;
      const turnUsername = (import.meta as any).env?.VITE_TURN_USERNAME as string | undefined;
      const turnCredential = (import.meta as any).env?.VITE_TURN_CREDENTIAL as string | undefined;
      if (turnUrl) {
        iceServers.push({ urls: turnUrl, username: turnUsername, credential: turnCredential });
      }

      const pc = new RTCPeerConnection({ iceServers });
      pcRef.current = pc;

      pc.onicecandidate = (e) => {
        if (e.candidate && callId) {
          addIceCandidate(callId, isCaller ? 'caller' : 'callee', e.candidate);
        }
      };
      pc.ontrack = (e) => setRemoteStream(e.streams[0]);

      const ms = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: type === 'video',
      });
      setLocalStream(ms);
      // Guard against race where pc was closed during getUserMedia
      if (pcRef.current !== pc || pc.signalingState === 'closed') {
        ms.getTracks().forEach((t) => t.stop());
      } else {
        ms.getTracks().forEach((t) => {
          try { pc.addTrack(t, ms); } catch { /* no-op if closed */ }
        });
      }

      if (isCaller) {
        const newId = callId || await createCall(callerId, calleeId, type);
        setCallId(newId);
        const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: type === 'video' });
        await pc.setLocalDescription(offer);
        await setOffer(newId, offer);

        unsubCall = subscribeCall(newId, async (rec) => {
          if (rec.answer && pc.currentRemoteDescription == null) {
            await pc.setRemoteDescription(rec.answer);
          }
          if (rec.endedAt) {
            teardown();
          }
        });

        unsubCalleeCandidates = subscribeIceCandidates(newId, 'callee', async (cand) => {
          try { await pc.addIceCandidate(cand); } catch {}
        });
      } else if (externalCallId) {
        setCallId(externalCallId);
        unsubCall = subscribeCall(externalCallId, async (rec) => {
          if (rec.offer && pc.currentRemoteDescription == null) {
            await pc.setRemoteDescription(rec.offer);
            const ans = await pc.createAnswer();
            await pc.setLocalDescription(ans);
            await setAnswer(externalCallId, ans);
          }
          if (rec.endedAt) {
            teardown();
          }
        });
        unsubCallerCandidates = subscribeIceCandidates(externalCallId, 'caller', async (cand) => {
          try { await pc.addIceCandidate(cand); } catch {}
        });
      }
    }

    function teardown() {
      unsubCall?.();
      unsubCallerCandidates?.();
      unsubCalleeCandidates?.();
      pcRef.current?.getSenders().forEach((s) => s.track?.stop());
      pcRef.current?.close();
      pcRef.current = null;
    }

    setup();
    return () => teardown();
  }, [callerId, calleeId, type, isCaller, externalCallId]);

  async function hangup() {
    if (callId) await endCall(callId);
  }

  return { callId, localStream, remoteStream, hangup };
}